import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Building2,
  ChevronLeft,
  ChevronRight,
  Home as HomeIcon,
  Pencil,
} from 'lucide-react';

import { ApiError, api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar } from '@/components/ui/avatar';
import { avatarUrl } from '@/lib/avatar-url';
import { cn, formatDuration, formatTime } from '@/lib/utils';
import { VoidedPunchesSection, type VoidedPunch } from '@/components/VoidedPunchesSection';
import { useAuth } from '@/lib/use-auth';
import { CorrectionRequestModal } from '@/pages/corrections/CorrectionRequestModal';

type WorkdayIncident = 'NO_OUT' | 'OUT_WITHOUT_IN' | 'DOUBLE_IN';
type WorkdayStatus = 'OPEN' | 'CLOSED' | 'INCIDENT';
type DeclaredLocation = 'OFFICE' | 'REMOTE';
type PunchType = 'IN' | 'OUT';
type Role = 'EMPLOYEE' | 'MANAGER' | 'HR_ADMIN' | 'SUPER_ADMIN';

interface RangeWorkday {
  /** null en días "teóricos" (sin Workday real en BD: festivos, días sin fichar
   *  para empleados que aún no tienen jornadas registradas). */
  id: string | null;
  date: string;
  expectedMinutes: number;
  workedMinutes: number;
  breakMinutes: number;
  balanceMinutes: number;
  status: WorkdayStatus;
}

interface RangeItem {
  workday: RangeWorkday;
  incidents: WorkdayIncident[];
}

interface RangeResponse {
  member: {
    id: string;
    fullName: string;
    email: string;
    role: Role;
    profileColor: string | null;
    avatarUpdatedAt: string | null;
  };
  from: string;
  to: string;
  items: RangeItem[];
  summary: {
    totalExpectedMinutes: number;
    totalWorkedMinutes: number;
    totalBreakMinutes: number;
    totalBalanceMinutes: number;
    daysOpen: number;
    daysClosed: number;
    daysIncident: number;
  };
}

interface DetailPunch {
  id: string;
  type: PunchType;
  eventAt: string;
  declaredLocation: DeclaredLocation;
  source: string;
}

interface DetailResponse {
  workday: RangeWorkday;
  incidents: WorkdayIncident[];
  punches: DetailPunch[];
  voidedPunches: VoidedPunch[];
}

const INCIDENT_LABEL: Record<WorkdayIncident, string> = {
  NO_OUT: 'Sin salida',
  OUT_WITHOUT_IN: 'Salida huérfana',
  DOUBLE_IN: 'Entrada doble',
};

const MONTH_NAMES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
];

export function EmployeeDay() {
  const { userId } = useParams<{ userId: string }>();
  const { user: viewer } = useAuth();
  const canEdit = viewer?.role === 'HR_ADMIN' || viewer?.role === 'SUPER_ADMIN';
  const [cursor, setCursor] = useState(() => {
    const n = new Date();
    return { year: n.getFullYear(), month: n.getMonth() };
  });
  // La fila desplegada se identifica por FECHA (YYYY-MM-DD), no por workdayId:
  // los días en los que el empleado no fichó nada no tienen fila Workday
  // (id === null) y RRHH debe poder abrirlos para registrar entrada y salida.
  const [openDayKey, setOpenDayKey] = useState<string | null>(null);

  const today = useMemo(() => new Date(), []);
  const isCurrentMonth = today.getFullYear() === cursor.year && today.getMonth() === cursor.month;

  const fromYmd = ymd(new Date(cursor.year, cursor.month, 1));
  const toYmd = ymd(new Date(cursor.year, cursor.month + 1, 0));

  const range = useQuery({
    queryKey: ['team', userId, 'workdays', 'range', fromYmd, toYmd],
    queryFn: () => api<RangeResponse>(`/team/${userId}/workdays/range?from=${fromYmd}&to=${toYmd}`),
    enabled: Boolean(userId),
  });

  const itemByDate = useMemo(() => {
    const map = new Map<string, RangeItem>();
    for (const it of range.data?.items ?? []) {
      map.set(it.workday.date.slice(0, 10), it);
    }
    return map;
  }, [range.data]);

  const days = useMemo(() => {
    const result: { date: Date; key: string; isToday: boolean; isFuture: boolean }[] = [];
    const last = new Date(cursor.year, cursor.month + 1, 0).getDate();
    for (let d = 1; d <= last; d++) {
      const date = new Date(cursor.year, cursor.month, d);
      const key = ymd(date);
      const isToday = sameYmd(date, today);
      const isFuture = date.getTime() > stripTime(today).getTime();
      result.push({ date, key, isToday, isFuture });
    }
    return result;
  }, [cursor, today]);

  function shiftMonth(delta: -1 | 1) {
    setOpenDayKey(null);
    setCursor((c) => {
      const n = new Date(c.year, c.month + delta, 1);
      return { year: n.getFullYear(), month: n.getMonth() };
    });
  }

  return (
    <div className="p-6 md:p-8">
      <div className="mb-3">
        <Link
          to="/equipo"
          className="inline-flex items-center gap-1.5 text-[12px] font-medium text-muted-foreground hover:text-icaria-600 transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Volver al equipo
        </Link>
      </div>

      <div className="bg-card border border-border rounded-sm shadow-md overflow-hidden">
        <header className="px-7 py-5 border-b border-border flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <Avatar
              fullName={range.data?.member.fullName ?? '·'}
              color={range.data?.member.profileColor ?? null}
              avatarUrl={
                range.data
                  ? avatarUrl(range.data.member.id, range.data.member.avatarUpdatedAt)
                  : null
              }
              size="md"
            />
            <div className="min-w-0">
              <h2 className="text-lg font-semibold tracking-tightish truncate">
                {range.data?.member.fullName ?? 'Jornada del empleado'}
              </h2>
              <p className="text-[12px] text-muted-foreground truncate">
                {range.data?.member.email ?? '—'} ·{' '}
                <span className="font-mono uppercase tracking-cap text-[10px]">
                  {canEdit ? 'edición RRHH' : 'vista read-only'}
                </span>
              </p>
            </div>
          </div>
          <div className="inline-flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => shiftMonth(-1)}
              aria-label="Mes anterior"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span
              className="font-mono text-[13px] font-medium uppercase text-muted-foreground px-3 min-w-[160px] text-center"
              style={{ letterSpacing: '0.1em' }}
            >
              {MONTH_NAMES[cursor.month]} {cursor.year}
            </span>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => shiftMonth(1)}
              disabled={isCurrentMonth}
              aria-label="Mes siguiente"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </header>

        <div className="p-7">
          {range.data ? <MonthSummary summary={range.data.summary} /> : null}

          {range.isLoading ? (
            <p className="text-sm text-muted-foreground mt-6">Cargando…</p>
          ) : range.error ? (
            <p role="alert" className="text-sm text-destructive mt-6">
              {range.error instanceof ApiError ? range.error.message : 'Error cargando jornadas'}
            </p>
          ) : (
            <div className="mt-6 -mx-7 overflow-x-auto">
              <table className="w-full text-[14px]">
                <thead>
                  <tr>
                    <Th>Día</Th>
                    <Th>Estado</Th>
                    <Th align="right">Esperado</Th>
                    <Th align="right">Trabajado</Th>
                    <Th align="right">Balance</Th>
                    <Th>Incidencias</Th>
                  </tr>
                </thead>
                <tbody>
                  {days.map((d) => {
                    const item = itemByDate.get(d.key);
                    const isOpen = openDayKey === d.key;
                    return (
                      <DayRow
                        key={d.key}
                        userId={userId!}
                        memberName={range.data?.member.fullName}
                        date={d.date}
                        dayKey={d.key}
                        item={item}
                        isToday={d.isToday}
                        isFuture={d.isFuture}
                        isOpen={isOpen}
                        onToggle={() => setOpenDayKey(isOpen ? null : d.key)}
                      />
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Th({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <th
      className={cn(
        'font-mono text-[12px] font-medium uppercase text-muted-foreground bg-paper border-b border-border px-5 py-3.5',
        align === 'right' ? 'text-right' : 'text-left',
      )}
      style={{ letterSpacing: '0.08em' }}
    >
      {children}
    </th>
  );
}

function MonthSummary({ summary }: { summary: RangeResponse['summary'] }) {
  const positive = summary.totalBalanceMinutes >= 0;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
      <Stat label="Esperado" value={formatDuration(summary.totalExpectedMinutes)} />
      <Stat label="Trabajado" value={formatDuration(summary.totalWorkedMinutes)} />
      <Stat
        label="Balance"
        value={`${positive ? '+' : '−'}${formatDuration(Math.abs(summary.totalBalanceMinutes))}`}
        tone={positive ? 'success' : 'destructive'}
      />
      <Stat
        label="Días con incidencia"
        value={String(summary.daysIncident)}
        tone={summary.daysIncident > 0 ? 'warning' : undefined}
      />
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'success' | 'destructive' | 'warning';
}) {
  const toneClass =
    tone === 'success'
      ? 'text-success'
      : tone === 'destructive'
        ? 'text-destructive'
        : tone === 'warning'
          ? 'text-warning'
          : 'text-foreground';
  return (
    <div className="border border-border rounded-sm bg-paper px-5 py-4.5">
      <p
        className="font-mono text-[11px] font-medium uppercase text-muted-foreground"
        style={{ letterSpacing: '0.12em' }}
      >
        {label}
      </p>
      <p
        className={cn(
          'font-mono text-[28px] font-medium tracking-tight2 mt-1.5 tabular-nums',
          toneClass,
        )}
      >
        {value}
      </p>
    </div>
  );
}

function DayRow({
  userId,
  memberName,
  date,
  dayKey,
  item,
  isToday,
  isFuture,
  isOpen,
  onToggle,
}: {
  userId: string;
  memberName?: string;
  date: Date;
  dayKey: string;
  item: RangeItem | undefined;
  isToday: boolean;
  isFuture: boolean;
  isOpen: boolean;
  onToggle: () => void;
}) {
  // Todo día no futuro es expandible, tenga o no fichajes: los días "teóricos"
  // (sin fichar) son justamente los que RRHH necesita poder corregir.
  const clickable = !isFuture;
  const weekday = date.toLocaleDateString('es-ES', { weekday: 'short' }).slice(0, 3).toUpperCase();
  return (
    <>
      <tr
        className={cn(
          'border-b border-border',
          isFuture && 'opacity-50',
          clickable && 'cursor-pointer hover:bg-paper',
          isToday && 'bg-icaria-100 hover:bg-icaria-100',
        )}
        onClick={clickable ? onToggle : undefined}
      >
        <td className="px-5 py-4">
          <span className="font-mono inline-flex items-baseline gap-2.5 tabular-nums">
            <b className="text-foreground font-semibold text-[15px]">
              {String(date.getDate()).padStart(2, '0')}
            </b>
            <span className="text-muted-foreground text-[12px]">{weekday}</span>
            {isToday ? (
              <span className="text-icaria-600 font-medium text-[12px] ml-0.5">hoy</span>
            ) : null}
          </span>
        </td>
        <td className="px-5 py-4">
          {item ? (
            <StatusPill status={item.workday.status} />
          ) : (
            <span className="text-sm text-muted-foreground">—</span>
          )}
        </td>
        <td className="px-5 py-4 text-right font-mono tabular-nums">
          {item ? formatDuration(item.workday.expectedMinutes) : '—'}
        </td>
        <td className="px-5 py-4 text-right font-mono tabular-nums">
          {item ? formatDuration(item.workday.workedMinutes) : '—'}
        </td>
        <td className="px-5 py-4 text-right font-mono tabular-nums">
          {item ? <BalanceCell minutes={item.workday.balanceMinutes} /> : '—'}
        </td>
        <td className="px-5 py-4 text-[13px] text-muted-foreground">
          {item && item.incidents.length > 0 ? (
            item.incidents.map((i) => INCIDENT_LABEL[i]).join(' · ')
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </td>
      </tr>
      {clickable && isOpen ? (
        <tr className="border-b border-border bg-paper/60">
          <td colSpan={6} className="px-7 py-5">
            <DayDetail
              userId={userId}
              memberName={memberName}
              workdayId={item?.workday.id ?? null}
              targetDate={item?.workday.date.slice(0, 10) ?? dayKey}
            />
          </td>
        </tr>
      ) : null}
    </>
  );
}

function StatusPill({ status }: { status: WorkdayStatus }) {
  const m: Record<WorkdayStatus, { label: string; tone: 'green' | 'neutral' | 'amber' }> = {
    OPEN: { label: 'Abierta', tone: 'green' },
    CLOSED: { label: 'Cerrada', tone: 'neutral' },
    INCIDENT: { label: 'Incidencia', tone: 'amber' },
  };
  const { label, tone } = m[status];
  return <Badge tone={tone}>{label}</Badge>;
}

function BalanceCell({ minutes }: { minutes: number }) {
  if (minutes === 0) return <span className="text-muted-foreground">0h 00m</span>;
  const positive = minutes > 0;
  return (
    <span className={positive ? 'text-success' : 'text-destructive'}>
      {positive ? '+' : '−'}
      {formatDuration(Math.abs(minutes))}
    </span>
  );
}

function DayDetail({
  userId,
  memberName,
  workdayId,
  targetDate,
}: {
  userId: string;
  memberName?: string;
  /** null en días sin ningún fichaje: no existe fila Workday todavía. */
  workdayId: string | null;
  targetDate: string;
}) {
  const queryClient = useQueryClient();
  const { user: viewer } = useAuth();
  const canEdit = viewer?.role === 'HR_ADMIN' || viewer?.role === 'SUPER_ADMIN';
  const [modalOpen, setModalOpen] = useState(false);

  const detail = useQuery({
    queryKey: ['team', userId, 'workday-detail', workdayId],
    queryFn: () => api<DetailResponse>(`/team/${userId}/workdays/${workdayId}`),
    enabled: workdayId !== null,
  });

  if (workdayId !== null) {
    if (detail.isLoading) {
      return <p className="text-xs text-muted-foreground">Cargando detalle…</p>;
    }
    if (detail.error) {
      return (
        <p className="text-xs text-destructive">
          {detail.error instanceof ApiError ? detail.error.message : 'Error cargando detalle'}
        </p>
      );
    }
    if (!detail.data) return null;
  }
  // Sin Workday no hay fichajes que mostrar, pero sí se puede corregir el día:
  // al aplicar la corrección el backend crea la jornada (findOrCreateForDate).
  const punches = detail.data?.punches ?? [];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p
          className="font-mono text-[10px] font-medium uppercase text-muted-foreground"
          style={{ letterSpacing: '0.14em' }}
        >
          Fichajes
        </p>
        {canEdit ? (
          <Button type="button" variant="outline" size="sm" onClick={() => setModalOpen(true)}>
            <Pencil className="h-3.5 w-3.5" />
            Corregir/Editar fichajes
          </Button>
        ) : null}
      </div>

      {punches.length === 0 ? (
        <p className="text-xs text-muted-foreground">Sin fichajes registrados.</p>
      ) : (
        <ul className="border border-border rounded-sm bg-card overflow-hidden">
          {punches.map((p, idx) => (
            <li
              key={p.id}
              className={cn(
                'flex items-center justify-between px-3 py-2 text-sm',
                idx > 0 && 'border-t border-border',
              )}
            >
              <div className="flex items-center gap-2.5">
                <Badge tone={p.type === 'IN' ? 'green' : 'neutral'}>{p.type}</Badge>
                <span className="font-mono tabular-nums">{formatTime(p.eventAt)}</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                {p.declaredLocation === 'OFFICE' ? (
                  <Building2 className="h-3.5 w-3.5" />
                ) : (
                  <HomeIcon className="h-3.5 w-3.5" />
                )}
                <span>{p.declaredLocation === 'OFFICE' ? 'Oficina' : 'Remoto'}</span>
              </div>
            </li>
          ))}
        </ul>
      )}

      <VoidedPunchesSection items={detail.data?.voidedPunches ?? []} />

      {canEdit ? (
        <CorrectionRequestModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          targetDate={targetDate}
          targetUserId={userId}
          targetName={memberName}
          punches={punches.map((p) => ({
            id: p.id,
            type: p.type,
            eventAt: p.eventAt,
            declaredLocation: p.declaredLocation,
          }))}
          onSubmitted={() => {
            queryClient.invalidateQueries({ queryKey: ['team', userId] });
          }}
        />
      ) : null}
    </div>
  );
}

function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function sameYmd(a: Date, b: Date): boolean {
  return ymd(a) === ymd(b);
}

function stripTime(d: Date): Date {
  const r = new Date(d);
  r.setHours(0, 0, 0, 0);
  return r;
}
